/**
 * Routes — arbre `createBrowserRouter` agrégé dynamiquement depuis le registry.
 *
 * @remarks
 * Quatre zones, dérivées du champ `manifest.routes[].zone` :
 *
 *  - **`'auth'`** : publique sans shell (`/login`...). Rendu sous `<AuthLayout>`,
 *    avec son propre `errorElement` (cf. `auth-zone.tsx`).
 *  - **`'public'`** : publique avec shell (rapport partageable). Rendu sous
 *    `<AppShell>` SANS `requireAuth`. `errorElement` par feature.
 *  - **`'public-bare'`** : publique sans shell, page standalone scrollable (CV
 *    public…). Rendu sous `<Outlet />` — la vue gère son propre layout. Pas de
 *    `requireAuth`.
 *  - **`'authenticated'`** (défaut) : auth requise. Rendu sous `<AppShell>` +
 *    loader `requireAuth`. `errorElement` par feature → crash isolé au lieu
 *    de remonter au RootErrorBoundary.
 *
 * **Zéro import statique de feature** côté shell — l'ajout d'une feature passe
 * uniquement par son manifest + `features-registration.ts`.
 *
 * **Factory pattern** : `createRouter()` est appelée APRÈS `registerFeatures()`
 * (cf. `App.tsx`). Ne pas exécuter au module load — le registry est vide.
 */

/* eslint-disable react-refresh/only-export-components */
import { createBrowserRouter, Outlet, redirect, type RouteObject } from 'react-router-dom';
import { BaseLayout } from '@/app/layout/BaseLayout';
import { AppShell } from '@/app/layout/AppShell';
import { buildAuthZone } from '@/app/router/auth-zone';
import { requireAuth } from '@/app/router/guards/require-auth';
import { SessionExpiredRedirect } from '@/app/router/SessionExpiredRedirect';
import { FeatureErrorFallback } from '@/app/router/FeatureErrorFallback';
import { useAuthStore } from '@/app/stores/use-auth-store';
import { featureRegistry, type RouteZone } from '@/app/registry';
import { Heading } from '@/shared/ui';

interface AggregatedRouteSet {
  readonly featureId: string;
  readonly label: string;
  readonly routes: readonly RouteObject[];
  readonly guard?: RouteObject['loader'];
}

/** Collecte tous les `FeatureRouteSet` du registry pour une zone donnée. */
function collectRouteSetsByZone(zone: RouteZone): AggregatedRouteSet[] {
  return featureRegistry.getAllFeatures().flatMap((manifest) =>
    (manifest.routes ?? [])
      .filter((rs) => (rs.zone ?? 'authenticated') === zone)
      .map<AggregatedRouteSet>((rs) => ({
        featureId: manifest.id,
        label: rs.errorFallbackLabel ?? manifest.id,
        routes: rs.routes,
        ...(rs.guard !== undefined && { guard: rs.guard }),
      })),
  );
}

/**
 * Enveloppe le groupe de routes d'une feature : erreur isolée, et garde
 * d'accès si la feature en déclare une.
 *
 * @remarks
 * Le `guard` se pose ici, sur le nœud de la feature, et non sur chaque route :
 * une feature protège une zone entière ou rien. Le shell ne sait pas ce que
 * garde ce loader — il le monte, c'est tout.
 */
function wrapRouteSet(rs: AggregatedRouteSet): RouteObject {
  return {
    element: <Outlet />,
    errorElement: <FeatureErrorFallback featureId={rs.featureId} featureLabel={rs.label} />,
    ...(rs.guard !== undefined && { loader: rs.guard }),
    children: rs.routes as RouteObject[],
  };
}

/**
 * Construit l'arbre de routes. À appeler **après** `registerFeatures()` —
 * en pratique au premier render d'`App` via `useState(() => createRouter())`.
 */
export function createRouter() {
  const authRouteSets = collectRouteSetsByZone('auth');
  const publicRouteSets = collectRouteSetsByZone('public');
  const publicBareRouteSets = collectRouteSetsByZone('public-bare');
  const authenticatedRouteSets = collectRouteSetsByZone('authenticated');

  return createBrowserRouter([
    {
      path: '/',
      element: <BaseLayout />,
      children: [
        {
          index: true,
          loader: () => {
            const isAuth = useAuthStore.getState().isAuthenticated;
            return redirect(isAuth ? '/tests' : '/login');
          },
        },

        // Zone 'auth' — publique sans shell. Un seul errorElement pour toute
        // la zone, rendu dans l'AuthLayout : une erreur ici ne doit jamais
        // atteindre le RootErrorBoundary (SAAS-984).
        buildAuthZone(authRouteSets.flatMap((rs) => rs.routes as RouteObject[])),

        // Zone 'public-bare' — publique sans shell (CV, pages standalone
        // scrollables). Pas de layout wrapper — la vue gère son scroll et fond.
        {
          element: <Outlet />,
          children: publicBareRouteSets.map(wrapRouteSet),
        },

        // Zone 'public' — publique avec shell. Wrapper errorElement par feature
        // pour qu'un crash dans le rapport public ne casse pas l'app entière.
        {
          element: <AppShell />,
          children: publicRouteSets.map(wrapRouteSet),
        },

        // Zone 'authenticated' — loader auth bloque AVANT le rendu (pas de
        // flash de contenu privé). Chaque feature a son errorElement isolé, et
        // son `guard` si elle en déclare un (rôle requis, cf. SAAS-883).
        // Une session qui expire en cours d'usage renvoie vers /login (SAAS-1266).
        {
          element: (
            <>
              <SessionExpiredRedirect />
              <AppShell />
            </>
          ),
          loader: requireAuth,
          children: authenticatedRouteSets.map(wrapRouteSet),
        },

        // 404
        { path: '*', element: <NotFoundPlaceholder /> },
      ],
    },
  ]);
}

function NotFoundPlaceholder() {
  return (
    <div className="bg-bg text-fg flex min-h-screen items-center justify-center">
      <div className="text-center">
        <Heading level="page" className="text-3xl">
          404
        </Heading>
        <p className="text-fg-muted mt-2 text-sm">Page introuvable</p>
      </div>
    </div>
  );
}
